import { Box, Button, FormControl, FormErrorMessage, Grid, HStack, Input, Text } from '@chakra-ui/react'
import { CreateMySpotInput, TopMySpotItemFragment } from 'generated/graphql'
import { formatDate } from 'helper/dateFormat'
import { FC } from 'react'
import { useForm } from 'react-hook-form'

type Props = {
  spot?: TopMySpotItemFragment
  disabled?: boolean
  isSubmitting: boolean
  onSubmit: (input: CreateMySpotInput) => Promise<TopMySpotItemFragment | undefined>
  onDelete: () => void
}

export const mySpotFormValidation = {
  googleMapUrl: {
    required: '必須です',
    maxLength: { value: 512, message: '512文字以内で入力してください' },
  },
  title: {
    required: '必須です',
    maxLength: { value: 20, message: '20文字以内で入力してください' },
  },
} as const

export const TopMySpotForm: FC<Props> = ({ spot, disabled, isSubmitting, onSubmit, onDelete }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CreateMySpotInput>({
    mode: 'onSubmit',
    defaultValues: spot ? { googleMapUrl: spot.googleMapUrl, title: spot.title } : { googleMapUrl: '', title: '' },
  })

  const submit = async (input: CreateMySpotInput) => {
    const savedSpot = await onSubmit(input)
    if (savedSpot) reset({ googleMapUrl: savedSpot.googleMapUrl, title: savedSpot.title })
  }

  return (
    <Box as="form" onSubmit={handleSubmit(submit)} borderBottomWidth="1px" borderColor="gray.100">
      <Grid templateColumns="minmax(220px, 1.1fr) minmax(260px, 1.2fr) 110px 170px 156px" alignItems="start">
        <FormControl p="12px" isRequired isInvalid={!!errors.title} isDisabled={disabled || isSubmitting}>
          <Input
            {...register('title', mySpotFormValidation.title)}
            type="text"
            aria-label="スポットタイトル"
            placeholder="スポットタイトルを入力"
          />
          <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
        </FormControl>

        <FormControl p="12px" isRequired isInvalid={!!errors.googleMapUrl} isDisabled={disabled || isSubmitting}>
          <Input
            {...register('googleMapUrl', mySpotFormValidation.googleMapUrl)}
            type="text"
            aria-label="Google Map URLまたは緯度・経度"
            placeholder="Google Map URL または 緯度, 経度"
          />
          <FormErrorMessage>{errors.googleMapUrl?.message}</FormErrorMessage>
        </FormControl>

        <Text p="24px 12px" whiteSpace="nowrap">
          {spot ? formatDate(new Date(spot.createdAt), 'yyyy/M/d') : '-'}
        </Text>

        <Text p="24px 12px" wordBreak="break-word">
          {spot?.createdByName ?? '-'}
        </Text>

        <HStack p="12px" spacing="8px" justify="flex-end">
          <Button type="submit" size="sm" variant="outline" isDisabled={disabled || !isDirty} isLoading={isSubmitting}>
            保存
          </Button>
          <Button type="button" size="sm" colorScheme="red" isDisabled={isSubmitting} onClick={onDelete}>
            削除
          </Button>
        </HStack>
      </Grid>
    </Box>
  )
}
